import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
// components
import { linkPropTypes } from '../../components/Link';
// styles
import {
  ColStyled,
  ContainerStyled,
  DivStyledBackgroundContainer,
  LinkStyledButton,
  RowStyled,
  SectionStyled,
  TypographyStyledDescription,
  TypographyStyledHeading,
  TypographyStyledProductName,
} from './styles';

export const HeroSectionComponent = ({
  backgroundImageAriaLabel,
  backgroundImageDesktop,
  backgroundImageDesktopLowRes,
  backgroundImageDesktopWebp,
  backgroundImageMobile,
  backgroundImageMobileLowRes,
  backgroundImageMobileWebp,
  backgroundImageTablet,
  backgroundImageTabletLowRes,
  backgroundImageTabletWebp,
  backgroundVariant,
  branding,
  className,
  ctaPrimary,
  ctaSecondary,
  descriptionText,
  headingText,
  productName,
  sectionId,
  themeType,
}) => {
  const theme = useContext(ThemeContext);
  const largeUp = theme.largeUp();

  const getBackgroundImageContainer = (
    <DivStyledBackgroundContainer
      id={`background-image-${sectionId}`}
      className='hero-section-bg-image-container'
      bgImageLargeUpDefault={backgroundImageDesktop}
      bgImageLargeUpLowRes={backgroundImageDesktopLowRes}
      bgImageLargeUpWebp={backgroundImageDesktopWebp}
      bgImageXSmallUpDefault={backgroundImageMobile}
      bgImageXSmallUpLowRes={backgroundImageMobileLowRes}
      bgImageXSmallUpWebp={backgroundImageMobileWebp}
      bgImageMediumUpDefault={backgroundImageTablet}
      bgImageMediumUpLowRes={backgroundImageTabletLowRes}
      bgImageMediumUpWebp={backgroundImageTabletWebp}>
      <span
        role='img'
        aria-label={backgroundImageAriaLabel} />
    </DivStyledBackgroundContainer>
  );

  return (
    <SectionStyled
      id={`section-${sectionId}`}
      className={className}
      role='banner'
      data-module={`section-${sectionId}:hero-module:A`}>
      {!largeUp && (
        getBackgroundImageContainer
      )}
      <ContainerStyled>
        <RowStyled>
          <ColStyled
            medium={6}
            backgroundVariant={backgroundVariant}
            branding={branding}
            themeType={themeType}>
            <>
              <TypographyStyledProductName
                id={`hero-product-name-${sectionId}`}
                className='hero-product-name'
                component='h1'
                themeType={themeType}
                variant='p'>
                {productName}
              </TypographyStyledProductName>
              <TypographyStyledHeading
                id={`hero-heading-text-${sectionId}`}
                className='hero-heading-text'
                align='left'
                component='h2'
                themeType={themeType}
                variant='h1'>
                {headingText}
              </TypographyStyledHeading>
              {descriptionText && (
                <TypographyStyledDescription
                  id={`hero-description-text-${sectionId}`}
                  className='hero-description-text'
                  align='left'
                  themeType={themeType}>
                  {descriptionText}
                </TypographyStyledDescription>
              )}
              {ctaPrimary && (
                <LinkStyledButton
                  id={`${sectionId}-cta-primary`}
                  className='hero-link-cta-primary'
                  ariaLabel={ctaPrimary.ariaLabel}
                  isInternalPdf={ctaPrimary.isInternalPdf && !ctaPrimary.isOutgoingLink}
                  isOutgoingLink={ctaPrimary.isOutgoingLink && !ctaPrimary.isInternalPdf}
                  linkUrl={ctaPrimary.linkUrl}
                  themeType={themeType}
                  scrollConfig={ctaPrimary.scrollConfig}
                  variant='buttonLinkPrimary'
                >
                  {ctaPrimary.buttonText} {/** change this to ctaPrimary.children in 4.x.x */}
                </LinkStyledButton>
              )}
              {ctaSecondary && (
                <LinkStyledButton
                  id={`${sectionId}-cta-secondary`}
                  className='hero-link-cta-secondary'
                  themeType={themeType}
                  variant={ctaSecondary.variant || 'buttonLinkSecondary'}
                  applyTopPadding
                  isInternalPdf={ctaSecondary.isInternalPdf && !ctaSecondary.isOutgoingLink}
                  isOutgoingLink={ctaSecondary.isOutgoingLink && !ctaSecondary.isInternalPdf}
                  linkUrl={ctaSecondary.linkUrl}
                  ariaLabel={ctaSecondary.ariaLabel}
                  scrollConfig={ctaSecondary.scrollConfig}
                  endAdornment={ctaSecondary.endAdornment || ''}
                  endAdornmentStylesReset={ctaSecondary.endAdornment || false}>
                  {ctaSecondary.buttonText} {/** change this to ctaSecondary.children in 4.x.x */}
                </LinkStyledButton>
              )}
            </>
          </ColStyled>
        </RowStyled>
      </ContainerStyled>
      {largeUp && (
        getBackgroundImageContainer
      )}
    </SectionStyled>
  );
};

HeroSectionComponent.propTypes = {
  /** required ariaLabel for background image */
  backgroundImageAriaLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  /** href or src of desktop jpg image to load */
  backgroundImageDesktop: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  /** Webp version of backgroundImage desktop */
  backgroundImageDesktopWebp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** base64 encoded desktop 'fuzzy' image for initial display before full image renders */
  backgroundImageDesktopLowRes: PropTypes.any.isRequired,
  /** base64 encoded tablet 'fuzzy' image for initial display before full image renders */
  backgroundImageTabletLowRes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** href or src of tablet jpg image to load */
  backgroundImageTablet: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** Webp version of backgroundImage Tablet */
  backgroundImageTabletWebp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** base64 encoded mobile 'fuzzy' image for initial display before full image renders */
  backgroundImageMobileLowRes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** href or src of mobile jpg image to load */
  backgroundImageMobile: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  /** Webp version of backgroundImage Mobile */
  backgroundImageMobileWebp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** backgroundVariant allows section to be different background color based on themeType */
  backgroundVariant: PropTypes.oneOf(['darkDefault', 'lightA', 'lightB', 'lightC']),
  /** sets overall branding of component or module, default='nm' */
  branding: PropTypes.oneOf(['nm', 'pcg']),
  /** optional additional className */
  className: PropTypes.string,
  /** Props passed to primary CTA button - if object not provided, will not show button */
  ctaPrimary: PropTypes.shape({
    buttonText: PropTypes.oneOfType([ // deprecated
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    ...linkPropTypes,
  }),
  /** Props passed to secondary CTA button - if object not provided, will now show button */
  ctaSecondary: PropTypes.shape({
    buttonText: PropTypes.oneOfType([ // deprecated
      PropTypes.string,
      PropTypes.object,
    ]),
    ...linkPropTypes,
  }),
  /** Optional Description Text */
  descriptionText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  /** Required Heading Text (h2) */
  headingText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  /** Required Product Name (h1) */
  productName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  /** required section id */
  sectionId: PropTypes.string.isRequired,
  /** Optional themeType */
  themeType: PropTypes.oneOf(['lightTheme', 'darkTheme', 'nmx-pcg']),
};

HeroSectionComponent.defaultProps = { ctaSecondary: null };

export default HeroSectionComponent;
